import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type RefObject,
} from "react";
import CloudDone from "@mui/icons-material/CloudDone";
import CloudOff from "@mui/icons-material/CloudOff";
import CloudQueue from "@mui/icons-material/CloudQueue";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useFormikContext } from "formik";
import { debounce } from "lodash-es";
import type { ResultAsync } from "neverthrow";
import { createPortal } from "react-dom";
import { match } from "ts-pattern";
import { z } from "zod";

import { createInitialValues } from "@thalamos/vision-common";

export type AutoSaveProps<Schema extends z.ZodType> = {
  debug: debug.Debugger;
  debugPortalRef?: RefObject<HTMLElement | null>;
  debounceIntervalMs?: number;
  strictFieldSchemas: Record<string, z.ZodType>;
  schema: z.ZodType;
  onAutoSave?: (
    value: Partial<Record<keyof z.infer<Schema>, unknown>>,
  ) => ResultAsync<true, string>;
};

export const AutoSave = <Schema extends z.ZodType>({
  debug,
  debugPortalRef,
  debounceIntervalMs = 1_000,
  strictFieldSchemas,
  schema,
  onAutoSave,
}: AutoSaveProps<Schema>) => {
  const { values, touched } = useFormikContext<z.infer<Schema>>();

  // Used for debugging purposes.
  const [lastStatus, setLastStatus] = useState<{
    status: "success" | "error";
    message: string;
    data?: Record<string, unknown>;
  } | null>(null);

  // We use this to drive the auto save icon.
  const [autoSaveState, setAutoSaveState] = useState<
    | "disabled" // No auto save function provided
    | "clean" // No changes to save
    | "saving" // Saving the data
    | "save-success" // Save was successful
    | "save-failure-user" // Save failed because of user error. The field is probably red which should draw their attention.
    | "save-failure-platform" // Save failed because of platform error (maybe network unavailable), so draw their attention to this red icon.
  >(onAutoSave === undefined ? "disabled" : "clean");

  const autoSaveFeedbackIcon = useMemo(
    () =>
      match(autoSaveState)
        .with("disabled", () => null)
        .with("clean", () => <CloudQueue />)
        .with("saving", () => (
          <Box
            sx={{
              display: "grid",
              alignItems: "center",
              justifyItems: "center",
            }}
          >
            <CloudQueue sx={{ gridColumnStart: "1", gridRowStart: "1" }} />
            <CircularProgress
              size={8}
              sx={{ gridColumnStart: "1", gridRowStart: "1" }}
            />
          </Box>
        ))
        .with("save-success", () => <CloudDone color="success" />)
        .with("save-failure-user", () => <CloudOff />)
        .with("save-failure-platform", () => <CloudOff color="error" />)
        .exhaustive(),
    [autoSaveState],
  );

  /**
   * Maybe auto save the provided values depending on the validity of the data.
   */
  const onMaybeAutoSave = useCallback(
    (currentValues: typeof values) => {
      // No point in processing the data if there is no callback.
      if (onAutoSave === undefined) {
        return;
      }

      setLastStatus(null);

      // Iterate over all of the fields and do the following:
      // - If the field value is "" or null, omit if from the object. This represents a value which is not yet set.
      // - If the field has a value, put it through the Zod schema to validate it.
      // - If the field is invalid, do not attempt to auto-save the form. This ensures that the form data is "valid but partial".
      let validatedValues: Partial<typeof values> = {};
      for (const [key, value] of Object.entries(currentValues as z.ZodObject)) {
        // Ignore empty values.
        if (value === "" || value === null) {
          continue;
        }

        // Parse provided value through the strict field schema.
        const fieldSchema = strictFieldSchemas[key] as z.ZodType | undefined;
        if (fieldSchema === undefined) {
          const issue = `Aborting auto-save as field "${key}" not found in strictFieldSchemas`;
          setLastStatus({
            status: "error",
            message: issue,
          });
          debug(issue);

          // Abandon the autosave
          setAutoSaveState("save-failure-user");
          return;
        }

        const validationResult = fieldSchema.safeParse(value);
        if (!validationResult.success) {
          const issue = `Aborting auto-save as field "${key}" is invalid`;
          setLastStatus({
            status: "error",
            message: issue,
            data: z.treeifyError(validationResult.error),
          });
          debug(issue, z.treeifyError(validationResult.error));

          // Abandon the autosave
          setAutoSaveState("save-failure-user");
          return;
        } else {
          validatedValues = {
            ...validatedValues,
            [key]: validationResult.data,
          };
        }
      }

      // Only auto save if there are candidate values to save.
      if (Object.keys(validatedValues).length === 0) {
        setAutoSaveState("clean");
      } else {
        // If all of the mandatory fields are valid, then we would like
        // to auto-save a more complete version of the form which includes
        // nulls for the optional fields. We do this by attempting to parse
        // through the schema, and if it succeeds, we use the parsed value.
        const parsedValue = schema.safeParse(
          createInitialValues(strictFieldSchemas, validatedValues),
        );

        // Bail if any of the reported errors relate to the fields we are trying to save.
        if (
          !parsedValue.success &&
          Object.keys(validatedValues).some((key) =>
            parsedValue.error.issues.some((issue) => issue.path.includes(key)),
          )
        ) {
          const issue = `Aborting auto-save as field is invalid`;
          setLastStatus({
            status: "error",
            message: issue,
            data: z.treeifyError(parsedValue.error),
          });
          debug(issue, z.treeifyError(parsedValue.error));

          // Abandon the autosave
          setAutoSaveState("save-failure-user");
          return;
        }

        const valuesToAutosave = parsedValue.success
          ? parsedValue.data
          : validatedValues;

        setLastStatus({
          status: "success",
          message: "Auto save initiated",
          data: valuesToAutosave as Record<string, unknown>,
        });

        // Fire-and-forget: orTee surfaces failures via auto-save state.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        void onAutoSave(valuesToAutosave as any)
          .andTee(() => {
            setAutoSaveState("save-success");
          })
          .orTee((error) => {
            setAutoSaveState("save-failure-platform");
            debug("Autosave failed", error);
          });
      }
    },
    [onAutoSave, strictFieldSchemas, debug, schema],
  );

  /**
   * A debounced version of the onMaybeAutoSave function.
   */
  const onMaybeAutoSaveDebounced = useCallback(
    debounce(onMaybeAutoSave, debounceIntervalMs),
    [onMaybeAutoSave, debounceIntervalMs],
  );

  /**
   * Whenever the values change, maybe auto save.
   * Debounced as this will fire on every keystroke.
   * Does not fire until some value is touched (avoiding a "save on mount")
   */
  useEffect(() => {
    if (Object.values(touched).some((value) => value)) {
      setLastStatus(null);
      setAutoSaveState(onAutoSave === undefined ? "disabled" : "saving");
      onMaybeAutoSaveDebounced(values);
    }
  }, [onMaybeAutoSaveDebounced, values, touched, onAutoSave]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          justifyContent: "flex-end",
          alignSelf: "center",
          marginRight: "0.5rem",
        }}
      >
        {autoSaveFeedbackIcon}
      </Box>
      {debug.enabled &&
        debugPortalRef !== undefined &&
        debugPortalRef.current !== null &&
        createPortal(
          <Stack
            sx={{
              padding: "0.25rem",
              borderRadius: "0.25rem",
              backgroundColor: debug.color,
            }}
          >
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                Auto Save
              </AccordionSummary>
              <AccordionDetails>
                <Stack>
                  <Button
                    variant="contained"
                    onClick={() => {
                      onMaybeAutoSave(values);
                    }}
                  >
                    Auto Save Now
                  </Button>
                  {onAutoSave === undefined && (
                    <Typography>Auto save is disabled for this form</Typography>
                  )}
                  {lastStatus !== null && (
                    <>
                      {lastStatus.status === "success" ? (
                        <Typography>{`⏳ ${lastStatus.message}`}</Typography>
                      ) : (
                        <Typography>{`❌ ${lastStatus.message}`}</Typography>
                      )}

                      <Typography
                        sx={{
                          color: debug.color,
                          fontFamily: "monospace",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {JSON.stringify(lastStatus.data, undefined, 2)}
                      </Typography>
                    </>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>,
          debugPortalRef.current,
        )}
    </>
  );
};
